<template>
  <q-card flat class="q-pa-md">
    <div class="row q-pb-xs">
      <div
        class="text-subtitle1 text-bold"
        :class="$q.dark.mode ? '' : ' text-primary'"
      >
        Broker Analytics Pro of {{ symbol }}
      </div>
      <q-space />
      <div class="q-gutter-sm">
        <q-select
          dense
          outlined
          class="q-mr-sm"
          v-model="transactionModel"
          :options="transactionList"
          label="Transaction Type"
          style="width: 210px; padding-bottom: 10px"
        />
      </div>

      <q-space />
      <div class="row">
        <div class="q-px-xs col-sm-">
          <q-input
            dense
            filled
            v-model="from_date"
            mask="date"
            :rules="['date']"
            hint="Enter From Date"
          >
            <template v-slot:append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy
                  cover
                  transition-show="scale"
                  transition-hide="scale"
                >
                  <q-date v-model="from_date">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Close" color="primary" flat />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
        </div>

        <div class="q-px-xs col-sm-">
          <q-input
            dense
            filled
            v-model="to_date"
            mask="date"
            :rules="['date']"
            hint="Enter To Date"
          >
            <template v-slot:append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy
                  cover
                  transition-show="scale"
                  transition-hide="scale"
                >
                  <q-date v-model="to_date">
                    <div class="row items-center justify-end">
                      <q-btn v-close-popup label="Close" color="primary" flat />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
        </div>

        <div class="q-pa-xs text-end col-sm-">
          <q-btn
            class="q-pa-sm"
            square
            color="blue-5"
            icon="search"
            @click="loadData(symbol)"
            size="sm"
          >
            <q-tooltip class="bg-primary">Search</q-tooltip>
          </q-btn>
        </div>
      </div>
    </div>

    <PackageAuth>
      <template #authorized-content>
        <div>
          <q-table
            v-if="transactionModel === 'Purchase'"
            :class="
              $q.dark.mode
                ? 'table-sticky-header-column-dark '
                : 'table-sticky-header-column table-column-color1'
            "
            flat
            dense
            bordered
            :rows="rowDataComputed"
            :columns="
              transactionModel === 'Purchase' ? purchaseColumns : sellColumns
            "
            binary-state-sort
            :pagination="{
              rowsPerPage: 20,
              sortBy: 'net_quantity',
              descending: true,
            }"
            separator="cell"
            :filter="filterBroker"
            :filter-method="filterBrokerData"
          >
            <template v-slot:top-left>
              <div class="flex">
                <span>
                  <q-input
                    borderless
                    dense
                    debounce="300"
                    v-model="filterBroker"
                    placeholder="Filter Broker"
                  >
                    <template v-slot:append>
                      <q-icon name="search" />
                    </template>
                  </q-input>
                </span>
              </div>
            </template>

            <template v-slot:body-cell-buyer="props">
              <q-td :props="props">
                <q-item
                  class="row justify-center"
                  dense
                  clickable
                  :to="{
                    path: '/brokers-analytics-pro',
                    query: { brokerID: props.value, transaction: 'Purchase' },
                  }"
                >
                  <div class="col">
                    {{ props.value }}
                    <q-tooltip>{{
                      brokerLists.find(
                        (broker) => broker.broker_no === props.value
                      )?.broker_name
                    }}</q-tooltip>
                  </div>
                </q-item>
              </q-td>
            </template>

            <template v-slot:body-cell-seller="props">
              <q-td :props="props">
                <q-item
                  class="row justify-center"
                  dense
                  clickable
                  :to="{
                    path: '/brokers-analytics-pro',
                    query: { brokerID: props.value, transaction: 'Selling' },
                  }"
                >
                  <div class="col">
                    {{ props.value }}
                    <q-tooltip>{{
                      brokerLists.find(
                        (broker) => broker.broker_no === props.value
                      )?.broker_name
                    }}</q-tooltip>
                  </div>
                </q-item>
              </q-td>
            </template>

            <template v-slot:top-row>
              <q-tr
                class="text-bold"
                :class="$q.dark.mode ? 'bg-grey-8' : 'bg-blue-'"
              >
                <q-td
                  class="text-center"
                  :class="$q.dark.mode ? 'bg-grey-8' : 'bg-blue-'"
                  >Total</q-td
                >
                <q-td class="text-center">
                  {{ amountConversion(sumBy(rowDataComputed, "buy_quantity")) }}
                </q-td>
                <q-td class="text-center">
                  {{
                    amountConversion(sumBy(rowDataComputed, "avg_buy_amount"))
                  }}
                </q-td>
                <q-td class="text-center">
                  {{
                    (
                      sumBy(rowDataComputed, "avg_buy_amount") /
                      sumBy(rowDataComputed, "buy_quantity")
                    )?.toFixed(2)
                  }}
                </q-td>
                <q-td class="text-center"> - </q-td>
                <q-td class="text-center">
                  {{
                    amountConversion(sumBy(rowDataComputed, "sell_quantity"))
                  }}
                </q-td>
                <q-td class="text-center">
                  {{
                    amountConversion(sumBy(rowDataComputed, "avg_sell_amount"))
                  }}
                </q-td>
                <q-td class="text-center">
                  {{
                    (
                      sumBy(rowDataComputed, "avg_sell_amount") /
                      sumBy(rowDataComputed, "sell_quantity")
                    )?.toFixed(2)
                  }}
                </q-td>
                <q-td class="text-center">
                  {{ amountConversion(sumBy(rowDataComputed, "net_quantity")) }}
                </q-td>
                <q-td class="text-center">
                  {{ amountConversion(sumBy(rowDataComputed, "net_amount")) }}
                </q-td>
                <q-td class="text-center">
                  {{
                    (
                      sumBy(rowDataComputed, "net_amount") /
                      sumBy(rowDataComputed, "net_quantity")
                    )?.toFixed(2)
                  }}
                </q-td>
              </q-tr>
            </template>
          </q-table>

          <q-table
            v-else
            :class="
              $q.dark.mode
                ? 'table-sticky-header-column-dark '
                : 'table-sticky-header-column table-column-color1'
            "
            flat
            dense
            bordered
            :rows="rowDataComputed"
            :columns="
              transactionModel === 'Purchase' ? purchaseColumns : sellColumns
            "
            binary-state-sort
            :pagination="{
              rowsPerPage: 20,
              sortBy: 'net_quantity',
              descending: false,
            }"
            separator="cell"
            :filter="filterBroker"
            :filter-method="filterBrokerData"
          >
            <template v-slot:top-left>
              <div class="flex">
                <span>
                  <q-input
                    borderless
                    dense
                    debounce="300"
                    v-model="filterBroker"
                    placeholder="Filter Broker"
                  >
                    <template v-slot:append>
                      <q-icon name="search" />
                    </template>
                  </q-input>
                </span>
              </div>
            </template>

            <template v-slot:top-row>
              <q-tr
                class="text-bold"
                :class="$q.dark.mode ? 'bg-grey-9' : 'bg-primary'"
              >
                <q-td class="text-center">Total</q-td>
                <q-td class="text-center">
                  {{ amountConversion(sumBy(rowDataComputed, "buy_quantity")) }}
                </q-td>
                <q-td class="text-center">
                  {{
                    amountConversion(sumBy(rowDataComputed, "avg_buy_amount"))
                  }}
                </q-td>
                <q-td class="text-center">
                  {{
                    (
                      sumBy(rowDataComputed, "avg_buy_amount") /
                      sumBy(rowDataComputed, "buy_quantity")
                    )?.toFixed(2)
                  }}
                </q-td>
                <q-td class="text-center"> - </q-td>
                <q-td class="text-center">
                  {{
                    amountConversion(sumBy(rowDataComputed, "sell_quantity"))
                  }}
                </q-td>
                <q-td class="text-center">
                  {{
                    amountConversion(sumBy(rowDataComputed, "avg_sell_amount"))
                  }}
                </q-td>
                <q-td class="text-center">
                  {{
                    (
                      sumBy(rowDataComputed, "avg_sell_amount") /
                      sumBy(rowDataComputed, "sell_quantity")
                    )?.toFixed(2)
                  }}
                </q-td>
                <q-td class="text-center">
                  {{ amountConversion(sumBy(rowDataComputed, "net_quantity")) }}
                </q-td>
                <q-td class="text-center">
                  {{ amountConversion(sumBy(rowDataComputed, "net_amount")) }}
                </q-td>
                <q-td class="text-center">
                  {{
                    (
                      sumBy(rowDataComputed, "net_amount") /
                      sumBy(rowDataComputed, "net_quantity")
                    )?.toFixed(2)
                  }}
                </q-td>
              </q-tr>
            </template>
          </q-table>
        </div>

        <q-card flat bordered class="q-pa-sm q-mt-sm row">
          <div class="col-xs-12 col-sm-6 col-md-4">
            <div class="text-body2 text-bold">
              Brokerwise Net Quantity of {{ symbol }}
            </div>
            <VueApexCharts
              :options="netChartOptions"
              :series="netSeriesComputed"
              type="bar"
              height="350"
            />
          </div>
          <div class="col-xs-12 col-sm-6 col-md-4">
            <div class="text-body2 text-bold">
              Brokerwise Buy Quantity of {{ symbol }}
            </div>
            <VueApexCharts
              :options="buyChartOptions"
              :series="buySeriesComputed"
              type="bar"
              height="350"
            />
          </div>
          <div class="col-xs-12 col-sm-6 col-md-4">
            <div class="text-body2 text-bold">
              Brokerwise Sell Quantity of {{ symbol }}
            </div>
            <VueApexCharts
              :options="sellChartOptions"
              :series="sellSeriesComputed"
              type="bar"
              height="350"
            />
          </div>
        </q-card>
      </template>
    </PackageAuth>
  </q-card>
</template>

<script setup>
import { ref, computed, watchEffect, onMounted } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";

import useData from "src/use/data";

import { sumBy } from "lodash";
import useUtils from "src/use/utils";
import VueApexCharts from "vue3-apexcharts";

import PackageAuth from "src/components/Packages/PackageAuth.vue";

const props = defineProps({
  theme: { type: Boolean, default: false },
  symbol: {
    type: String,
    required: true,
  },
});

const store = useStore();
const $q = useQuasar();

const { brokerList, brokerAnalyticsWithSymbol } = useData();
const { amountConversion } = useUtils();

const brokerLists = ref([]);

const buyBrokerAnalyticsData = ref([]);
const sellBrokerAnalyticsData = ref([]);

const transactionModel = ref("Purchase");
const transactionList = ref(["Purchase", "Selling"]);

const to_date = ref(store.state.market.asOf.replaceAll("/", "-"));
const parsedDate = new Date(to_date.value);
const from_date = ref(
  new Date(
    parsedDate.getFullYear(),
    parsedDate.getMonth() - 1,
    parsedDate.getDate() + 2
  )
    .toISOString()
    .substring(0, 10)
);

const columns = [
  {
    name: "buyer",
    required: true,
    label: "Buyer",
    field: "buyer",
    sortable: true,
    format: (val, row) => {
      return val;
    },
    align: "center",
  },

  {
    name: "buy_quantity",
    label: "Buy Qnty",
    field: (row) => row.buy_quantity,
    format: (val, row) =>
      `${amountConversion(val)} ( ${row.buy_quantity_percentage?.toFixed(
        2
      )} %)`,

    sortable: true,
    align: "center",
  },
  {
    name: "avg_buy_amount",
    label: "Avg Buy Amt",
    field: "avg_buy_amount",
    field: (row) => amountConversion(row.avg_buy_amount),
    sortable: true,
    align: "center",
  },
  {
    name: "net_buy_rate",
    label: "Net Buy Rate",
    field: "net_buy_rate",
    sortable: true,
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
  {
    name: "seller",
    required: true,
    label: "Seller",
    field: "seller",
    sortable: true,
    format: (val, row) => {
      return val;
    },
    align: "center",
  },
  {
    name: "sell_quantity",
    label: "Sell Qnty",
    field: (row) => row.sell_quantity,
    format: (val, row) =>
      `${amountConversion(val)} ( ${row.sell_quantity_percentage?.toFixed(
        2
      )} %)`,

    sortable: true,
    align: "center",
  },
  {
    name: "avg_sell_amount",
    label: "Avg Sell Amt",
    field: (row) => row.avg_sell_amount,
    format: (val) => amountConversion(val),

    sortable: true,
    align: "center",
  },
  {
    name: "net_sell_rate",
    label: "Net Sell Rate",
    field: "net_sell_rate",
    sortable: true,
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
  {
    name: "net_amount",
    label: "Net Amt",
    field: "net_amount",
    // field: (row) => amountConversion(row.net_amount),
    sortable: true,
    align: "center",
  },

  {
    name: "net_rate",
    label: "Net Rate",
    field: "net_rate",
    sortable: true,
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
];

const purchaseColumns = [
  ...columns.slice(0, 8),
  {
    name: "net_quantity",
    label: "Net Holding",
    field: (row) => row.net_quantity,
    format: (val, row) =>
      `${amountConversion(val)} ( ${row.net_quantity_percentage?.toFixed(2)}%)`,
    sortable: true,
    align: "center",
  },
  ...columns.slice(8),
];

const sellColumns = [
  ...columns.slice(0, 8),
  {
    name: "net_quantity",
    label: "Net Supply",
    field: (row) => row.net_quantity,
    format: (val, row) =>
      `${amountConversion(val)} ( ${row.net_quantity_percentage?.toFixed(
        2
      )} %)`,
    sortable: true,
    sort: (a, b, rowA, rowB) => parseInt(a, 10) - parseInt(b, 10),
    align: "center",
  },
  ...columns.slice(8),
];

const filterBroker = ref("");
const filterBrokerData = (rows, terms, cols, getCellValue) => {
  return rows.filter((row) => {
    return row.buyer.toLowerCase().includes(filterBroker.value.toLowerCase());
  });
};

function processData(data, filterCondition) {
  const filteredData = data.filter(filterCondition);

  const total_net_quantity = filteredData.reduce((total, current) => {
    return total + current.net_quantity;
  }, 0);
  const total_buy_quantity = filteredData.reduce((total, current) => {
    return total + current.buy_quantity;
  }, 0);
  const total_sell_quantity = filteredData.reduce((total, current) => {
    return total + current.sell_quantity;
  }, 0);

  return filteredData.map((item) => {
    const net_quantity_percentage =
      (item.net_quantity / total_net_quantity) * 100;

    const buy_quantity_percentage =
      (item.buy_quantity / total_buy_quantity) * 100;

    const sell_quantity_percentage =
      (item.sell_quantity / total_sell_quantity) * 100;

    return {
      ...item,
      net_quantity_percentage,
      buy_quantity_percentage,
      sell_quantity_percentage,
    };
  });
}

function loadData(symbol) {
  $q.loading.show({});
  brokerAnalyticsWithSymbol(
    from_date.value.replaceAll("/", "-"),
    to_date.value.replaceAll("/", "-"),
    symbol
  )
    .then((res) => {
      buyBrokerAnalyticsData.value = processData(
        res.data,
        (i) => i.net_amount > 0
      );
      sellBrokerAnalyticsData.value = processData(
        res.data,
        (i) => i.net_amount < 0
      );
    })
    .catch((error) => {})
    .finally(() => {
      $q.loading.hide();
    });
}

const buyBrokerAnalyticsDataComputed = computed(() => {
  return buyBrokerAnalyticsData.value
    .slice()
    .sort((a, b) => -1 * (a.net_amount - b.net_amount));
});

const sellBrokerAnalyticsDataComputed = computed(() => {
  return sellBrokerAnalyticsData.value
    .slice()
    .sort((a, b) => 1 * (a.net_amount - b.net_amount));
});

const rowDataComputed = computed(() => {
  return transactionModel.value === "Purchase"
    ? buyBrokerAnalyticsDataComputed.value
    : sellBrokerAnalyticsDataComputed.value;
});

function getChartData(field) {
  const sourceData =
    transactionModel.value === "Purchase"
      ? buyBrokerAnalyticsDataComputed.value
      : sellBrokerAnalyticsDataComputed.value;

  return [...sourceData]
    .sort((a, b) =>
      transactionModel.value === "Selling" && field === "net_quantity"
        ? a[field] - b[field]
        : b[field] - a[field]
    )
    .slice(0, 10);
}

function getSeriesData(field) {
  const data = getChartData(field);
  return [{ data: data.map((item) => item[field]) }];
}

function getChartOptions(categories, type) {
  const textColor = $q.dark.mode ? "#fff" : "#000";

  return {
    chart: {
      type: "bar",
      height: 350,
      events: {
        dataPointSelection: false,
        markerClick: false,
        legendClick: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "75%",
        endingShape: "rounded",
        dataLabels: { position: "top" },
      },
    },
    tooltip: { theme: $q.dark.mode ? "dark" : "light" },
    legend: {
      show: true,
      position: "top",
      labels: { colors: textColor },
    },
    xaxis: {
      categories: categories,
      title: {
        text: type,
        style: { color: textColor },
      },
      labels: { style: { colors: textColor } },
    },
    yaxis: {
      title: {
        text: "Quantity",
        style: { color: textColor },
      },
      labels: {
        formatter: (val) => amountConversion(val),
        offsetY: -40,
        style: { colors: textColor },
      },
    },
  };
}

const buySeriesComputed = computed(() => getSeriesData("buy_quantity"));
const sellSeriesComputed = computed(() => getSeriesData("sell_quantity"));
const netSeriesComputed = computed(() => getSeriesData("net_quantity"));

const buyChartOptions = computed(() => {
  const data = getChartData("buy_quantity");
  return getChartOptions(
    data.map((item) => item.buyer),
    "Buyer"
  );
});
const sellChartOptions = computed(() => {
  const data = getChartData("sell_quantity");
  return getChartOptions(
    data.map((item) => item.buyer),
    "Seller"
  );
});
const netChartOptions = computed(() => {
  const data = getChartData("net_quantity");
  const type =
    transactionModel.value === "Selling" ? "Net Release" : " Net Holding";
  return getChartOptions(
    data.map((item) => item.buyer),
    type
  );
});

watchEffect(() => {
  loadData(props.symbol);
});

onMounted(async () => {
  brokerLists.value = (await brokerList()).data;
  loadData(props.symbol);
});
</script>
